import { useCallback, useEffect, useMemo, useState } from "react";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbar,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import { Box } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";

import formatDateTime from "../../../utils/dateFormate";
import { appColors } from "../../../constant/constant";
import { API } from "../../../api/API";
import BookIcon from "@mui/icons-material/Book";
import UpdatestructureModal from "../modals/UpdateStructure";
import ProfilStructureModal from "../modals/profilStructure";
import DeleteDialog from "../modals/DeleteModal";
import { ToastContainer, toast } from "react-toastify";
import DesactiveStructureModal from "../modals/DesactivetStructure";
import RegisterStructure from "../modals/RegisterStructure";

// Action types
const actionTypes = {
  voir: "Voir",
  modifier: "Modifier",
  desactive: "Activer/Desactive",
  delete: "Delete",
};
const iconList = [
  <VisibilityOutlinedIcon sx={{ color: appColors.secondary }} />,
  <TipsAndUpdatesIcon sx={{ color: appColors.secondary }} />,
  <BookIcon sx={{ color: appColors.warning }} />,

  /*  <TipsAndUpdatesIcon sx={{ color: appColors.secondary }} />,*/
  <DeleteOutlineOutlinedIcon sx={{ color: appColors.danger }} />,
];

const DataGridStructure = () => {
  const [isLoading, setisLoading] = useState(true);
  const [selectedRow, setselectedRow] = useState(null);
  const [pagination, setpagination] = useState({ page: 1, limit: 50 });
  const [rows, setRows] = useState([]);
  /** state pour actions */
  const [voirState, setVoirState] = useState(null);
  const [modifierState, setModifierState] = useState(null);
  const [desactiveState, setDesactiveState] = useState(null);
  const [deleteState, setDeleteState] = useState(null);

  const handleAction = useCallback((row, actionType) => {
    setselectedRow(row);
    switch (actionType) {
      case actionTypes.voir:
        setVoirState(true);
        break;
      case actionTypes.modifier:
        setModifierState(true);
        break;
      case actionTypes.desactive:
        setDesactiveState(true);
        break;
      case actionTypes.delete:
        setDeleteState(true);
        break;
      default:
        break;
    }
  }, []);

  const handleDeleteConfirm = async (id) => {
    try {
      const res = await API.delete(`/structures/${id}`);
      handleRefresh();
    } catch (error) {
      toast(`Impossible de supprimer la structure: ${error.message}`, {
        type: "error",
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      console.error("Failed to delete structure:", error.message);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "order",
        headerName: "#",
        width: 80,
      },
      {
        field: "intitule",
        headerName: "Intitulé",
      },
      {
        field: "code",
        headerName: "Code",
      },
      {
        field: "rccm",
        headerName: "RCCM",
      },
      {
        field: "ifu",
        headerName: "IFU",
      },
      {
        field: "tel",
        headerName: "Téléphone",
      },
      {
        field: "adresse",
        headerName: "Adresse",
      },
      {
        field: "email",
        headerName: "Email",
      },
      {
        field: "type",
        headerName: "Type",
      },
      {
        field: "isActive",
        headerName: "Actif",
        type: "boolean",
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: params.value ? appColors.success : appColors.warning,
            }}
          >
            {params.value ? "Actif" : "Inactif"}
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: "Créé le",
        width: 160,
        valueGetter: (value, rows) => formatDateTime(value),
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",

        width: 80,
        getActions: (params) =>
          Object.entries(actionTypes).map((i, index) => {
            return (
              <GridActionsCellItem
                icon={iconList[index]}
                label={`${i[1]}`}
                key={i[0]}
                onClick={() => handleAction(params.row, i[1])}
                showInMenu
              />
            );
          }),
      },
    ],
    [handleAction]
  );

  useEffect(() => {
    getStructure();
  }, []);

  const handleRefresh = () => {
    setisLoading(true);
    getStructure();
  };

  const handleDesactiveConfirm = async () => {
    try {
      const { id } = selectedRow;
      if (!id) {
        toast.error("ID de la structure introuvable", {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        return;
      }
      const res = await API.patch(`/structures/deactivate/${id}`);
      if (res.status === 200) {
        toast.success(
          `Structure ${selectedRow.isActive ? "desactivée" : "activée"} avec succès`,
          {
            position: "top-right",
            autoClose: 2000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          }
        );
        handleRefresh();
      }
    } catch (error) {
      toast.error(
        `Impossible de ${selectedRow.isActive ? "desactiver" : "activer"} la structure: ${error.message}`,
        {
          position: "top-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      console.error("Failed to desactivate structure:", error.message);
    }
  };
  const getStructure = async () => {
    try {
      const response = await API.get(`/structures`);
      if (response.status === 200) {
        const { data } = response.data;
        const _data = data.map((row, index) => ({
          ...row,
          order: index + 1,
        }));
        setRows(_data);
      }
    } catch (error) {
      console.error("Failed to fetch structures:", error);
    } finally {
      setisLoading(false);
    }
  };

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ backgroundColor: "#f5f5f5", color: "#37B049" }}>
      <GridToolbar
        sx={{
          "& .MuiButton-root": {
            color: "#37B049",
          },
          "& .MuiSvgIcon-root": {
            color: "#37B049",
          },
        }}
      />
      <RegisterStructure handleRefresh={handleRefresh} />
    </GridToolbarContainer>
  );

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <ToastContainer />
      <DataGrid
        slots={{
          toolbar: () => <CustomToolbar />,
        }}
        columns={columns}
        rows={rows}
        checkboxSelection
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: pagination.limit,
            },
          },
        }}
        pageSizeOptions={[pagination.limit]}
        loading={isLoading}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
        sx={{
          minWidth: "1000px", // Adjust this value based on your column widths
          overflowX: "auto", // Ensure horizontal scrolling
        }}
      />
      {modifierState && selectedRow && (
        <>
          <UpdatestructureModal
            show={modifierState}
            handleClose={() => setModifierState(false)}
            structure={selectedRow}
            onUpdateSuccess={handleRefresh}
          />
        </>
      )}
      {voirState && selectedRow && (
        <ProfilStructureModal
          show={voirState}
          handleClose={() => setVoirState(false)}
          structure={selectedRow}
        />
      )}
      {desactiveState && selectedRow && (
        <DesactiveStructureModal
          show={desactiveState}
          handleClose={() => setDesactiveState(false)}
          handleDesactiveConfirm={handleDesactiveConfirm}
          structure={selectedRow}
        />
      )}
      {/* Delete confirmation dialog */}
      {deleteState && selectedRow && (
        <DeleteDialog
          isDeleteDialogOpen={deleteState}
          setIsDeleteDialogOpen={() => setDeleteState(false)}
          handleDeleteConfirm={() => {
            handleDeleteConfirm(selectedRow.id);
          }}
        />
      )}
    </Box>
  );
};

export default DataGridStructure;
